"use client";

import React from "react";
import { defaultTheme } from "@/src/config/colors";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  icon?: React.ReactNode;
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  children,
  icon,
  isLoading = false,
  className = "",
  ...props
}) => {
  const baseStyles = "font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2";

  const variants = {
    primary: `bg-[${defaultTheme.primary}] text-[${defaultTheme.text.primary}] hover:bg-[${defaultTheme.primaryHover}]`,
    secondary: `bg-[${defaultTheme.secondary}] text-[${defaultTheme.text.inverse}] hover:bg-[${defaultTheme.secondaryLight}]`,
    outline: `border-2 border-[${defaultTheme.primary}] text-[${defaultTheme.primary}] hover:bg-[${defaultTheme.primary}] hover:text-[${defaultTheme.text.primary}]`,
    ghost: `text-[${defaultTheme.primary}] hover:bg-[${defaultTheme.surface}]`,
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className} ${
        isLoading ? "opacity-70 cursor-not-allowed" : ""
      }`}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {icon && !isLoading && icon}
      {isLoading && <span className="animate-spin">⏳</span>}
      {children}
    </button>
  );
};
